import { apiRequest, apiUrl, ApiError } from "./client.js";
import {
  clearSession,
  storeSession,
  sessionToken,
  type OperatorSession,
} from "./session-store.js";

/**
 * Signing in and signing out, and nothing else — because the product mounts nothing
 * else. Better Auth implements sign-up, password reset, password change and account
 * management; the API deliberately does not put them on the network
 * (`apps/api/src/app.ts:211-218`), so this app has no screen for any of them.
 */

export interface SignInInput {
  email: string;
  password: string;
}

interface SignInBody {
  token?: unknown;
  user?: { id?: unknown; email?: unknown; name?: unknown } | null;
}

/**
 * The bearer arrives in `set-auth-token`, which is the header the API declares and
 * exposes for exactly this (`openapi.ts:109-114`, `app.ts:208`). The body carries it
 * too, so the header is read first and the body is the fallback — either way what is
 * kept is the token, never the password.
 */
export function readSignIn(
  header: string | null,
  body: unknown,
): OperatorSession | null {
  const parsed = (
    typeof body === "object" && body !== null ? body : {}
  ) as SignInBody;
  const token =
    header && header !== ""
      ? header
      : typeof parsed.token === "string" && parsed.token !== ""
        ? parsed.token
        : null;
  if (!token) return null;
  const user = parsed.user ?? {};
  const userId = typeof user.id === "string" ? user.id : "";
  if (userId === "") return null;
  return {
    token,
    userId,
    email: typeof user.email === "string" ? user.email : "",
    name: typeof user.name === "string" ? user.name : "",
  };
}

export async function signIn(input: SignInInput): Promise<OperatorSession> {
  let response: Response;
  try {
    response = await fetch(apiUrl("/api/auth/sign-in/email"), {
      method: "POST",
      headers: {
        "content-type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify({ ...input, rememberMe: true }),
    });
  } catch {
    throw new ApiError(0, "the API could not be reached");
  }

  if (response.status === 429) {
    const seconds = response.headers.get("X-Retry-After");
    throw new ApiError(
      429,
      seconds
        ? `Too many sign-in attempts. Try again in ${seconds} seconds.`
        : "Too many sign-in attempts. Try again shortly.",
    );
  }
  if (response.status === 401 || response.status === 403) {
    throw new ApiError(response.status, "Email or password not recognised.");
  }
  if (!response.ok) {
    throw new ApiError(
      response.status,
      `Sign-in failed (HTTP ${String(response.status)}).`,
    );
  }

  const session = readSignIn(
    response.headers.get("set-auth-token"),
    await response.json().catch(() => null),
  );
  if (!session) {
    throw new ApiError(response.status, "Sign-in returned no session.");
  }
  storeSession(session);
  return session;
}

/**
 * The session is ended at the API first, so the bearer is dead on the server and not
 * merely forgotten here. A network failure on the way out still clears it locally —
 * a browser that cannot reach the API is not a browser that should stay signed in.
 */
export async function signOut(): Promise<void> {
  if (!sessionToken()) return;
  try {
    // Better Auth reads this route as JSON and refuses a POST that does not say so,
    // so the empty object is the request rather than an afterthought.
    await apiRequest("/api/auth/sign-out", { method: "POST", body: {} });
  } catch {
    // Already reported by the API being unreachable; the local clear stands.
  } finally {
    clearSession();
  }
}
